<template>
    <modal name="trial-banner-modal"
    v-bind="modalProps">
        <div class="modal-container">
            <div class="modal-header">
                <base-material-design-icon v-if="showCalendarWidget" icon="arrowLeft" class="modal-icon" @click="showCalendarWidget = false; renderKey = !renderKey" />
                <base-material-design-icon v-if="daysLeft" icon="close" class="modal-icon modal-icon-close" @click="closeModal()" />
            </div>
            <div v-show="showCalendarWidget" :key="renderKey">
                <iframe class="calendar-iframe" src="https://calendly.com/audiencekey/30min" width="100%" height="950" scrolling="no" frameborder="0"></iframe>
            </div>
            <div v-show="!showCalendarWidget" class="content">
                <p class="headline">You have <span class="days-left" :class="{ 'days-left-warning': daysLeftWarning }">{{ daysLeft }}</span> {{ daysLeftPluralLabel }} left in your trial</p>
                <div class="description">
                    <p>Need more time?</p>
                    We can help you with that! Schedule a quick call to extend your trial or receive support to get the most out of your trial
                </div>
                <div class="buttons">
                    <input v-if="daysLeft" class="button button-deny" @click="closeModal()" type="button" value="I'm ok, thanks">
                    <input class="button" @click="showCalendarWidget = true" type="button" value="Yes! Let's schedule a quick call">
                    <input class="button" @click="navigateToBilling()" type="button" value="View Pricing">
                </div>
                <div class="checklist-actions" v-if="daysLeft">
                    <span class="checklist-text">You have {{ checklistItemsCount || 'no' }} remaining {{ checklistItemsPluralLabel }} in your 
                        <router-link
                            :to="{'name': 'account-checklist'}"
                            @click.native="closeModal()"
                            class="checklist-link">
                            onboarding checklist
                        </router-link>
                    </span>
                </div>
            </div>
        </div>
    </modal>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'

export default {
  name: 'TrialModal',
  computed: {
    ...mapState('rankZoneCommon', ['trial']),
    ...mapState(['unhashedClientId']),
    shouldShowModal () {
        if (this.$route.name.startsWith('setup-wizard')) {
            return false
        }
        // If trial is over
        if (!this.daysLeft) {
            return true
        }
        // If 2 hours have passed since the user closed the banner then show it again.
        const prevCloses = JSON.parse(
            localStorage.getItem('trial_banner_closed')
        )
        if (prevCloses && prevCloses[this.unhashedClientId]) {
            const lastBannerClosedTime = parseInt(
                prevCloses[this.unhashedClientId]
            )
            const currentTime = new Date().getTime()
            const timeDifference = currentTime - lastBannerClosedTime
            const hoursDifference = timeDifference / (1000 * 60 * 60)
            // If it has been more than 2 hours since last seen
            if (hoursDifference > 2) {
                return true
            }
            return false
        }
        // If it has not been seen yet
        return true

    },
    modalProps () {
      const modalProps = {
        width: '75%',
        height: 'auto'
      }
      return { ...baseModalProps, ...modalProps }
    },
    daysLeft () {
        return this.trial.trialDaysLeft > 0 ? this.trial.trialDaysLeft : 0
    },
    daysLeftPluralLabel () {
        if (this.daysLeft !== 1) {
            return 'days'
        }
        return 'day'
    },
    daysLeftWarning () {
        return this.daysLeft < 6
    },
    checklistItemsPluralLabel () {
        if (this.checklistItemsCount !== 1) {
            return 'items'
        }
        return 'item'
    }
  },
  data () {
    return {
        checklistItemsCount: 0,
        showCalendarWidget: false,
        renderKey: false
    }
  },
  async mounted () {
    if (this.shouldShowModal) {
        await this.getChecklistItemsCount()
        this.showModal()
    }
  },
  methods: {
    ...mapActions('accountSetup', ['loadChecklist']),
    async getChecklistItemsCount () {
        const checklist = await this.loadChecklist(this.clientId)
        this.checklistItemsCount = Object.keys(checklist).filter(key => key.endsWith('_count')).reduce((prev, cur) => {
            if (checklist[cur] === 0) {
                prev += 1
            }
            return prev
        }, 0)
    },
    showModal () {
        this.$modal.show('trial-banner-modal')
    },
    navigateToBilling () {
        this.$router.push('/system/billing')
        this.closeModal()
    },
    closeModal () {
        const prevCloses =
            JSON.parse(localStorage.getItem('trial_banner_closed')) || {}
        prevCloses[this.unhashedClientId] = new Date().getTime()
        localStorage.setItem('trial_banner_closed', JSON.stringify(prevCloses))
        this.$modal.hide('trial-banner-modal')
    }
  },
  watch: {
    shouldShowModal (bool) {
        if (bool) {
            this.showModal()
        }
    }
  }
}
</script>

<style lang="scss" scoped>

.modal-container {
    margin: 10% 0;
}
.modal-header {
    display: flex;
    justify-content: space-between;
    width: 100%;
}
.modal-icon {
    margin: 15px;
    color: $rfx-darker-grey;
}

.modal-icon-close {
    margin-left: auto;
}

.content {
    padding: 1% 10%;
    display: flex;
    flex-direction: column;
    width: 80%;
    align-items: center;
    justify-content: space-evenly;
}
.headline {
    color: $rfx-bright-turquoise;
    font-size: 40px;
    margin: 10px 0;
}
.days-left {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $rfx-bright-turquoise;
    color: #FFF;
    border-radius: 10px;
    padding: 10px;
    line-height: 40px;
    min-width: 40px;
    min-height: 40px;
    display: inline-block;
    text-align: center;
}

.days-left-warning {
    background-color: $rfx-warning-red;
}

.description {
    text-align: center;
    margin-top: 0;
    font-size: 18px;
}
.buttons {
    width: 90%;
    margin: 10px;
    margin-top: 40px;
    display: flex;
    justify-content: space-between;
}

.button {
    border: 1px solid $rfx-lighter-blue;
    border-radius: 6px;
    // width: 340px;
    height: 50px;
    cursor: pointer;
    background-color: $rfx-lighter-blue;
    color: #FFF;
    font-size: 16px;
    flex: 1 1 0px;
    margin: 0 1%;
}

.button-deny {
    border: 1px solid $rfx-lighter-blue;
    background-color: #FFF;
    color: $rfx-lighter-blue;
}

.checklist-actions {
    margin: 20px;
}

.checklist-text {
    color: $rfx-lighter-blue;
    font-size: 18px;
}
.checklist-link {
    color: $rfx-link-color;
    text-decoration: underline;
    font-size: 18px;
    cursor: pointer;
}

.pricing-table {
    padding: 10% 0;
}
</style>
